import React, { useState, useEffect, useRef } from "react";
import { useAPI, Grid, Icon, Link, Card, Button } from "components/lib";
import { useParams } from "react-router-dom";

import { SurveyCreatorComponent, SurveyCreator } from "survey-creator-react";
import { setLicenseKey, Serializer, chooseFiles } from "survey-core";
import { PropertyGridEditorCollection } from "survey-creator-core";
import Axios from "axios";
import { slk } from "survey-core";
import "survey-core/defaultV2.css";
import "survey-creator-core/survey-creator-core.min.css";
import { stackgoTheme } from "components/survey/stackgo-theme";

import {
  DEFAULT_SCORING,
  RATING_QUESTION_NAME,
  SCORING_FORM_TYPES,
  ensureCalculatedSchema,
  hasQuestionNamed,
  insertManualRatingQuestion,
  normalizeScoringConfig,
  removeCalculatedScoring,
} from "./scoringTemplate";
import { attachScoring } from "../../verification/scoringRunner";
// the same check the public render path uses, so restore and render agree on
// what counts as a storable image rather than using a looser local rule
import {
  EMPTY_SNAPSHOT,
  resolveRestore,
  validateImageFile,
} from "./imageUpload";

slk(
    "YjExNDVhZmEtNTI2ZC00YjIwLTkyNDAtZGRiMzQwYjgzNmQ1OzE9MjAyNy0wNy0xNSwyPTIwMjctMDctMTUsND0yMDI3LTA3LTE1LDg9MjAyNy0wNy0xNQ=="
);

// scoring-related serializer properties (registered once per session)
let scoringPropertiesRegistered = false;
function registerScoringProperties() {
  if (scoringPropertiesRegistered) return;
  scoringPropertiesRegistered = true;

  if (!Serializer.findProperty("question", "isSystemLocked")) {
    Serializer.addProperty("question", {
      name: "isSystemLocked",
      type: "boolean",
      default: false,
      visible: false,
    });
  }

  if (!Serializer.findProperty("itemvalue", "score")) {
    Serializer.addProperty("itemvalue", {
      name: "score",
      type: "number",
      default: 0,
      category: "general",
      visibleIndex: 0,
    });
  }

  if (!Serializer.findProperty("boolean", "scoreTrue")) {
    Serializer.addProperty("boolean", {
      name: "scoreTrue",
      type: "number",
      default: 0,
      category: "general",
      displayName: "Score when Yes/True",
    });
  }

  if (!Serializer.findProperty("boolean", "scoreFalse")) {
    Serializer.addProperty("boolean", {
      name: "scoreFalse",
      type: "number",
      default: 0,
      category: "general",
      displayName: "Score when No/False",
    });
  }
}

registerScoringProperties();

// locked (system-managed) questions only expose these in the property grid
const LOCKED_QUESTION_ALLOWED_PROPS = ["name", "description", "visibleIf"];

// Accepted upload formats, stated up front because the Creator's own picker
// offers SVG and we do not accept it.
const IMAGE_UPLOAD_HINT =
  "Upload a PNG, JPEG or WebP (2MB max). SVG can't be uploaded — export it as a PNG, or paste a public https:// URL instead.";

// Mirrors helper/formImage.js — the server is authoritative, these exist so the
// common rejection never round-trips.
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_IMAGE_UPLOAD_BYTES = 2 * 1024 * 1024;


function parseMisc(misc) {
  if (!misc) return {};
  if (typeof misc === "string") {
    try {
      return JSON.parse(misc) || {};
    } catch (err) {
      return {};
    }
  }
  return misc;
}

function isLockedElement(element) {
  return element && typeof element.getPropertyValue === "function"
    ? element.getPropertyValue("isSystemLocked") === true
    : false;
}

export function FormBuilderView(props) {
  const params = useParams();
  const id = params.id;
  const formAPI = useAPI("/api/form/" + id);

  const [formCreatorMode, setFormCreatorMode] = useState("standard");
  const [readOnly, setReadOnly] = useState(true);
  const [formData, setFormData] = useState({});
  const [form, setForm] = useState({});
  const [ratingMode, setRatingMode] = useState("manual");
  const [scoringStatus, setScoringStatus] = useState(null);
  // uncontrolled on purpose: the SurveyCreator is recreated on every render
  // (see below), so driving these as state would remount the designer on every
  // keystroke. The panel mounts after the form loads, so defaultValue already
  // reflects the saved config; the refs are read at save / preview time.
  const thresholdMediumRef = useRef(null);
  const thresholdHighRef = useRef(null);
  // The theme lives in a ref for the same reason the thresholds do: the creator is
  // reconstructed on every render, so state would remount the designer. It is
  // refreshed on every theme edit (not just on save) so a re-render triggered by
  // the edit-mode toggle or the scoring panel cannot discard in-progress work.
  const themeRef = useRef(null);
  // The designer JSON lives in a ref for the same reason the theme does, and it
  // matters more: `creator.JSON = formData` below hydrates from LAST-SAVED state,
  // so without this ref any setState at all — an upload error, a scoring status,
  // a theme reset — silently discarded every unsaved designer edit. Refreshed on
  // every creator edit via onModified, so a rebuild re-hydrates with the work in
  // progress rather than without it.
  const designerJsonRef = useRef(null);
  // Survives the render between picking a file and the upload resolving.
  const uploadSnapshotRef = useRef({ ...EMPTY_SNAPSHOT });
  const [hasTheme, setHasTheme] = useState(false);
  const [themeStatus, setThemeStatus] = useState(null);
  const [themeError, setThemeError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const formRecord = formAPI?.data?.[0];
  const formMisc = parseMisc(formRecord?.misc);
  // scoring applies to risk_assessment and edd typed forms; misc.type is
  // system-managed (template install) — the scoring UI never writes it
  const scoringEligible = SCORING_FORM_TYPES.includes(formMisc?.type);
  const savedScoring = formRecord?.data?.scoring;

  Serializer.addProperty("question", {
    name: "private",
    type: "boolean",
    category: "general",
    default: false,
    visibleIndex: 0,
    onSetValue: (survey, value) => {
      // ...
      // You can perform required checks or modify the `value` here
      // ...
      // Set the `value`
      survey.setPropertyValue("private", value);
      // You can perform required actions after the `value` is set
      // ...
    },
  });

  Serializer.addProperty("question", {
    name: "tfn",
    type: "boolean",
    category: "general",
    default: false,
    visibleIndex: 0,
    onSetValue: (survey, value) => {
      // ...
      // You can perform required checks or modify the `value` here
      // ...
      // Set the `value`
      survey.setPropertyValue("tfn", value);
      // You can perform required actions after the `value` is set
      // ...
    },
  });

  Serializer.addProperty("survey", {
    name: "tfns",
    type: "single",
    category: "general",
    default: "",
    visibleIndex: 0,
    onSetValue: (survey, value) => {
      // ...
      // You can perform required checks or modify the `value` here
      // ...
      // Set the `value`
      survey.setPropertyValue("tfns", value);
      // You can perform required actions after the `value` is set
      // ...
    },
  });

  const creatorOptions = {
    showLogicTab: true,
    isAutoSave: false,
    // haveCommercialLicense: true,
    showJSONEditorTab: true,
    showThemeTab: true,
    pageEditMode: formCreatorMode,
    readOnly: readOnly,
  };

  useEffect(() => {
    if (!formAPI?.data?.[0]?.misc?.saveNo) {
      setReadOnly(true);
    }
    console.log("fomr data", formData);

    const record = formAPI?.data?.[0];
    const misc = parseMisc(record?.misc);
    const eligible = SCORING_FORM_TYPES.includes(misc?.type);
    const mode = misc?.scoring_mode === "calculated" ? "calculated" : "manual";
    setRatingMode(mode);

    let incoming = record?.data;
    if (eligible && mode === "calculated") {
      // calculated forms always carry the locked rating question
      incoming = ensureCalculatedSchema(incoming, incoming?.scoring);
    }
    // A load always wins over the ref: without this, the empty default scaffold
    // the creator synthesises on the first render could shadow the real form.
    designerJsonRef.current = null;
    setFormData(incoming);

    const storedTheme = record?.theme || null;
    const themed = !!(storedTheme && Object.keys(storedTheme).length);
    themeRef.current = themed ? storedTheme : null;
    setHasTheme(themed);
    // if (
    //   formAPI?.data &&
    //   !formAPI?.loading &&
    //   Object.keys(formData).length === 0
    // ) {
    //   console.log("updating form data");
    //   setFormData(formAPI?.data?.[0]?.data);
    // }
  }, [formAPI.loading]);

  const creator = new SurveyCreator(creatorOptions);

  // v1.11 has no applyCreatorTheme; feature-detect so the StackGo theme
  // applies automatically if the creator is ever upgraded.
  if (typeof creator.applyCreatorTheme === "function") {
    creator.applyCreatorTheme(stackgoTheme);
  }

  creator.JSON = designerJsonRef.current || formData;

  // Registered AFTER the hydration above on purpose — registering first would
  // capture the hydration itself as an edit.
  creator.onModified.add(() => {
    designerJsonRef.current = creator.JSON;
  });

  // Every path that replaces the designer's JSON goes through here. Calling
  // setFormData directly would be silently ineffective, because the hydration
  // above prefers the ref. The one exception is the load effect, which clears
  // the ref first precisely so the freshly loaded schema wins — if you add a
  // third writer, route it through here rather than inventing a fourth shape.
  const applyDesignerJson = (next) => {
    designerJsonRef.current = next;
    setFormData(next);
  };

  // Only hydrate when a theme was actually stored. SurveyJS's Themes tab always
  // has a preset selected, so assigning unconditionally would hand an un-themed
  // form a synthesised default (a blue) and the next ordinary survey save would
  // persist it — an un-themed form must stay un-themed and render defaultV2.
  if (themeRef.current && Object.keys(themeRef.current).length) {
    creator.theme = themeRef.current;
  }

  // Uploading an image used to be refused outright, because the only endpoint
  // available returned a five-minute signed URL — nothing durable to store.
  // POST /api/form/:id/image now writes a durable public object, so the refusal
  // is gone. See docs/plans/2026-09-16-001-fix-form-logo-upload-plan.md.
  //
  // THE TRAP, and the reason the failure path below looks the way it does:
  // three of survey-creator's five upload call sites IGNORE the callback status
  // and assign the second argument straight onto the property —
  // survey-creator-core.js:6424-6431 does `survey.logo = link` with the status
  // bound to `_`, and :8022 / :8034 do the same for a question's imageLink. So
  // passing an error MESSAGE as the second argument writes that sentence into
  // the survey and renders it as a broken <img src>. That was the bug. Never
  // pass anything but a URL or "".
  //
  // The property-grid strip stays blank on failure and cannot be fixed from
  // here: the grid rewraps whatever we pass into [{ content, file }]
  // (survey-creator-core.js:34140), survey-core pushes that OBJECT as the error
  // text (survey.core.js:39032-39041), and LocalizableString renders only
  // strings. See docs/solutions/ui-bugs/surveyjs-upload-error-message-never-renders.md.
  // The toast and the durable card line below carry the reason instead.

  // Snapshot the property's value BEFORE the picker opens. onOpenFileChooser
  // fires ahead of QuestionFileEditorModel.clear(), which is what makes it
  // possible to put a wiped logo back. Keyed by element and property: a bare
  // value would let a stale snapshot from one element restore a valid-looking
  // URL onto a different one.
  const takeSnapshot = (element, propertyName, value) => {
    uploadSnapshotRef.current = {
      element: element,
      propertyName: propertyName,
      value: typeof value === "string" ? value : "",
    };
  };

  // Consumed exactly once — see resolveRestore in ./imageUpload for why.
  const snapshotFor = (options) => {
    const snapshot = uploadSnapshotRef.current;
    uploadSnapshotRef.current = { ...EMPTY_SNAPSHOT };
    return resolveRestore(snapshot, options);
  };

  if (creator.onOpenFileChooser) {
    creator.onOpenFileChooser.add((sender, options) => {
      takeSnapshot(
        options.element,
        options.propertyName,
        options.element?.[options.propertyName]
      );

      // preserve the default behaviour exactly
      chooseFiles(options.input, options.callback);
    });
  }

  // The designer's property grid — which is where the Survey > Logo file editor
  // lives — never routes through creator.onOpenFileChooser: PropertyGridModel
  // wires only survey.onUploadFiles, so the picker resolves through survey-core's
  // own fallback. Without this the snapshot is never taken for that field, and a
  // failed upload would clear a logo the firm had already configured. The upload
  // side reports { element: question.obj, propertyName: question.name }, so key
  // the snapshot the same way.
  if (creator.onPropertyGridSurveyCreated) {
    creator.onPropertyGridSurveyCreated.add((sender, options) => {
      options.survey?.onOpenFileChooser?.add((gridSurvey, fileOptions) => {
        const propertyName = fileOptions.element?.name;

        takeSnapshot(options.obj, propertyName, options.obj?.[propertyName]);
        chooseFiles(fileOptions.input, fileOptions.callback);
      });
    });
  }

  if (creator.onUploadFile) {
    creator.onUploadFile.add(async (sender, options) => {
      const restore = snapshotFor(options);

      const fail = (message) => {
        // never a message — see the trap above
        options.callback("error", restore);

        // the property-grid route already wiped the value before we ran, and its
        // error branch does not assign, so put it back explicitly
        if (options.element && options.propertyName)
          options.element[options.propertyName] = restore;

        creator.notify?.(message, "error");
        setUploadError(message);
      };

      const file = options.files?.[0];

      const invalid = validateImageFile(file, {
        acceptedTypes: ACCEPTED_IMAGE_TYPES,
        maxBytes: MAX_IMAGE_UPLOAD_BYTES,
        hint: IMAGE_UPLOAD_HINT,
      });

      if (invalid) return fail(invalid);

      try {
        const body = new FormData();
        body.append("file", file, file.name);

        // bounded so the picker always leaves its uploading state
        const response = await Axios.post(`/api/form/${id}/image`, body, {
          timeout: 30000,
        });
        const url = response?.data?.data?.url;

        if (!url) return fail("The server did not return an image URL.");

        // Order matters: assign through the callback FIRST. setUploadError
        // rebuilds the creator, and this closure holds the OLD instance — so a
        // render before the callback would land the assignment on a discarded
        // survey. Assigning first also fires onModified synchronously, which
        // writes designerJsonRef, so the rebuild re-hydrates with the new image.
        options.callback("success", url);
        if (uploadError) setUploadError(null);
      } catch (err) {
        fail(
          err?.response?.data?.message ||
            "The image couldn't be uploaded. Please try again."
        );
      }
    });
  }

  // Capture edits as they happen; the creator instance does not survive a render.
  const trackTheme = () => {
    themeRef.current = creator.theme || null;
  };
  if (creator.themeEditor?.onThemeSelected) {
    creator.themeEditor.onThemeSelected.add(trackTheme);
  }
  if (creator.themeEditor?.onThemePropertyChanged) {
    creator.themeEditor.onThemePropertyChanged.add(trackTheme);
  }

  const propertyStopList = ["storeDataAsText", "valueName", "allowMultiple"];

  creator.onShowingProperty.add(function (sender, options) {
    if (propertyStopList.indexOf(options.property.name) != -1) {
      options.canShow = false;
      return;
    }

    if (
      isLockedElement(options.obj) &&
      LOCKED_QUESTION_ALLOWED_PROPS.indexOf(options.property.name) == -1
    ) {
      options.canShow = false;
      return;
    }

    options.canShow = true;
  });

  creator.onElementDeleting.add(function (sender, options) {
    if (isLockedElement(options.element)) {
      options.allowing = false;
    }
  });

  creator.onElementAllowOperations.add(function (sender, options) {
    if (isLockedElement(options.obj)) {
      options.allowDelete = false;
      options.allowCopy = false;
      options.allowChangeType = false;
      options.allowChangeRequired = false;
    }
  });

  creator.onQuestionAdded.add(function (sender, options) {
    var question = options.question;
    // Check if the added question is of type "file"
    if (question.getType() === "file") {
      // Set the storeDataAsText property to false by default
      question.storeDataAsText = false;
    }
  });

  const refValue = (ref, fallback) => {
    const value = ref?.current?.value;
    if (value === undefined || value === null || value === "") return fallback;
    return value;
  };

  const currentScoringConfig = () =>
    normalizeScoringConfig({
      threshold_medium: refValue(
        thresholdMediumRef,
        savedScoring?.threshold_medium ?? DEFAULT_SCORING.threshold_medium
      ),
      threshold_high: refValue(
        thresholdHighRef,
        savedScoring?.threshold_high ?? DEFAULT_SCORING.threshold_high
      ),
    });

  // live score preview inside the creator's Preview tab
  const attachPreviewScoring = (survey) => {
    if (!survey || !scoringEligible || ratingMode !== "calculated") return;
    attachScoring(survey, { scoring: currentScoringConfig() });
  };

  if (creator.onTestSurveyCreated) {
    creator.onTestSurveyCreated.add(function (sender, options) {
      attachPreviewScoring(options?.survey);
    });
  }

  if (creator.onSurveyInstanceCreated) {
    creator.onSurveyInstanceCreated.add(function (sender, options) {
      const area = options?.area || options?.reason;
      if (area && /preview|test|run/i.test(String(area))) {
        attachPreviewScoring(options?.survey);
      }
    });
  }

  const changeRatingMode = (mode) => {
    if (readOnly || mode === ratingMode) return;

    const json = creator.JSON || {};
    const next =
      mode === "calculated"
        ? ensureCalculatedSchema(json, currentScoringConfig())
        : removeCalculatedScoring(json);

    applyDesignerJson(next);
    setRatingMode(mode);
    setScoringStatus(null);
  };

  const insertRatingQuestion = () => {
    if (readOnly) return;

    const json = creator.JSON || {};
    if (hasQuestionNamed(json, RATING_QUESTION_NAME)) {
      alert('This form already has a "risk_rating" question.');
      return;
    }

    applyDesignerJson(insertManualRatingQuestion(json));
  };

  // Theme writes go to their own column and MUST NOT carry `data`: saveSurveyJSON
  // always sends the survey schema, and creator.JSON strips the unregistered root
  // key `scoring`, so routing a theme save through it would drop the scoring
  // config on manual forms and fail validation outright on calculated ones.
  const saveThemeJSON = async (theme) => {
    try {
      const response = await Axios.patch("/api/form/" + id, { theme: theme });
      if (response.status !== 200) return false;
      themeRef.current = theme;
      setHasTheme(!!(theme && Object.keys(theme).length));
      setThemeError(null);
      return true;
    } catch (err) {
      // The validator explains exactly which property was rejected. SurveyJS only
      // shows a generic "Error saving", so surface the real reason ourselves.
      setThemeError(
        err?.response?.data?.message ||
          "The theme could not be saved. Please try again."
      );
      return false;
    }
  };

  creator.saveThemeFunc = async (saveNo, callback) => {
    const ok = await saveThemeJSON(creator.theme || null);
    callback(saveNo, ok);
  };

  const resetTheme = async () => {
    if (readOnly) return;
    setThemeStatus("saving");
    const ok = await saveThemeJSON(null);
    setThemeStatus(ok ? "saved" : "error");
    // No reload: clearing the ref and re-rendering rebuilds the creator without a
    // theme, and a reload here would silently discard unsaved designer and
    // scoring-threshold edits.
  };

  creator.saveSurveyFunc = async (saveNo, callback) => {
    let payloadJSON = creator.JSON || {};

    if (scoringEligible) {
      if (ratingMode === "calculated") {
        const config = currentScoringConfig();
        if (!(config.threshold_high > config.threshold_medium)) {
          alert("High threshold must be greater than medium threshold.");
          callback(saveNo, false);
          return;
        }
        payloadJSON = ensureCalculatedSchema(payloadJSON, config);
      } else {
        payloadJSON = removeCalculatedScoring(payloadJSON);
      }
    }

    await saveSurveyJSON(
      "/api/form/" + id,
      payloadJSON,
      (formAPI?.data?.[0]?.misc?.saveNo || 0) + saveNo,
      callback
    );
  };

  const saveSurveyJSON = async (url, json, saveNo, callback) => {
    // spread the existing misc so system-managed keys (type,
    // source_template_id, installed_at) are never stripped by a save
    const miscPayload = { ...formMisc, saveNo: saveNo };
    if (scoringEligible) {
      miscPayload.scoring_mode = ratingMode;
    }

    try {
      // Axios resolves only on 2xx; the API returns 200 { message, data }
      // (no `success` field). A validation failure throws server-side → 5xx →
      // the catch below reports it.
      const response = await Axios.patch(url, {
        data: json,
        misc: miscPayload,
      });
      if (response.status === 200) {
        applyDesignerJson(json);
        if (saveError) setSaveError(null);
        callback(saveNo, true);
      } else {
        callback(saveNo, false);
      }
    } catch (err) {
      // SurveyJS only shows a generic "Error saving", so surface the real
      // reason ourselves — the same treatment saveThemeJSON already gets.
      // The server returns 500 { message } and Axios rejects on 5xx.
      setSaveError(
        err?.response?.data?.message ||
          "The form could not be saved. Please try again."
      );
      callback(saveNo, false);
    }
  };

  // Persist the scoring panel on its own — the SurveyJS Creator's Save button
  // only fires for in-editor survey edits, so scoring-only changes need an
  // explicit save. Reads the current editor JSON, embeds the scoring config /
  // strips it, and PATCHes (misc.scoring_mode is merged inside saveSurveyJSON).
  const saveScoringSettings = async () => {
    if (readOnly) return;

    let json = creator.JSON || {};

    if (ratingMode === "calculated") {
      const config = currentScoringConfig();
      if (!(config.threshold_high > config.threshold_medium)) {
        alert("High threshold must be greater than medium threshold.");
        return;
      }
      json = ensureCalculatedSchema(json, config);
    } else {
      json = removeCalculatedScoring(json);
    }

    setScoringStatus("saving");
    await saveSurveyJSON(
      "/api/form/" + id,
      json,
      (formMisc?.saveNo || 0) + 1,
      (no, ok) => setScoringStatus(ok ? "saved" : "error")
    );
  };

  return (
    <>
      <Grid cols={4}>
        <Card>
          <a href={`/account/forms`}>
            <Icon
              color="dark"
              image="arrow-left"
              size={20}
              className="absolute top-1/2 left-4 -translate-y-1/2"
            />
          </a>
          <div className="relative text-center min-h-max">
            <p className="text-2xl font-bold">{formAPI?.data?.[0]?.name}</p>
            <p className="text-xs text-slate-400">
              <Link url={`/account/forms`}>Back to forms</Link>
            </p>
          </div>
        </Card>
        <Card>
          <Icon
            color="dark"
            image="alert-triangle"
            size={20}
            className="absolute top-1/2 left-4 -translate-y-1/2"
          />
          <div
            className="relative text-center min-h-max"
            onClick={() =>
              readOnly ? setReadOnly(false) : window.location.reload()
            }
          >
            <p className="text-2xl font-bold">{readOnly ? "No" : "Yes"}</p>
            <p className="text-sm text-sg-blue">
              <Button
                text="Edit"
                textOnly
                className="underline text-sg-blue"
                action={() =>
                  readOnly ? setReadOnly(false) : window.location.reload()
                }
              />
            </p>
          </div>
        </Card>
        <Card>
          <Icon
            color="dark"
            image="layout"
            size={20}
            className="absolute top-1/2 left-4 -translate-y-1/2"
          />
          <div className="relative text-center min-h-max">
            <p
              className="text-2xl font-bold"
              onClick={() =>
                setFormCreatorMode(
                  formCreatorMode === "bypage" ? "standard" : "bypage"
                )
              }
            >
              {formCreatorMode === "bypage" ? "By Page" : "Standard"}
            </p>
            <p className="text-sm text-slate-400">
              <Button
                text="Mode"
                textOnly
                className="underline"
                action={() =>
                  setFormCreatorMode(
                    formCreatorMode === "bypage" ? "standard" : "bypage"
                  )
                }
              />
            </p>
          </div>
        </Card>
        <Card>
          <Icon
            color="dark"
            image="calendar"
            size={20}
            className="absolute top-1/2 left-4 -translate-y-1/2"
          />
          <div className="relative text-center min-h-max">
            <p className="text-2xl font-bold">
              {formAPI?.data?.[0]?.created_at.split("T")[0]}
            </p>
            <p className="text-sm text-slate-400">Date</p>
          </div>
        </Card>
      </Grid>

      {scoringEligible && (
        <Card title="Risk Scoring">
          <div className="space-y-4 text-sm text-slate-700">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-medium">Rating mode:</span>
              <select
                value={ratingMode}
                onChange={(event) => changeRatingMode(event.target.value)}
                disabled={readOnly}
                className="rounded border border-slate-300 bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="manual">Manual</option>
                <option value="calculated">Calculated</option>
              </select>
            </div>

            <p className="text-xs text-slate-500">
              {ratingMode === "calculated"
                ? 'Scores from radio, dropdown, checkbox and boolean questions are summed; the locked "risk_rating" question derives the rating from the thresholds below (score ≥ high → High, score ≥ medium → Medium, otherwise Low). Set per-choice scores in each question\'s Choices table (Score column); boolean questions use "Score when Yes/True" and "Score when No/False".'
                : 'Reviewers pick the rating themselves. Use "Insert rating question" to add the standard low/medium/high question so the rating is saved under the "risk_rating" convention.'}
            </p>

            <div
              className={
                ratingMode === "calculated"
                  ? "flex flex-wrap items-end gap-4"
                  : "hidden"
              }
            >
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-500">
                  Medium threshold (score ≥)
                </span>
                <input
                  ref={thresholdMediumRef}
                  type="number"
                  defaultValue={
                    savedScoring?.threshold_medium ??
                    DEFAULT_SCORING.threshold_medium
                  }
                  disabled={readOnly}
                  className="w-32 rounded border border-slate-300 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-500">
                  High threshold (score ≥)
                </span>
                <input
                  ref={thresholdHighRef}
                  type="number"
                  defaultValue={
                    savedScoring?.threshold_high ??
                    DEFAULT_SCORING.threshold_high
                  }
                  disabled={readOnly}
                  className="w-32 rounded border border-slate-300 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </label>
            </div>

            {ratingMode === "manual" && (
              <Button
                small
                text="Insert rating question"
                action={insertRatingQuestion}
              />
            )}

            {!readOnly && (
              <div className="flex items-center gap-3 pt-2">
                <Button
                  small
                  text="Save scoring settings"
                  action={saveScoringSettings}
                />
                {scoringStatus === "saving" && (
                  <span className="text-xs text-slate-500">Saving…</span>
                )}
                {scoringStatus === "saved" && (
                  <span className="text-xs text-sg-green">Saved</span>
                )}
                {scoringStatus === "error" && (
                  <span className="text-xs text-sg-red">Save failed</span>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Shown for the whole of edit mode, not just once a theme exists: the
          background-image guidance below is most needed by someone who has not
          themed the form yet, and the old (hasTheme || themeError) gate hid it
          from exactly those people. */}
      {!readOnly && (
        <Card title="Form Theme">
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
            {hasTheme ? (
              <span>
                This form has a saved theme. Public pages render it in these colours;
                resetting returns the form to the standard styling.
              </span>
            ) : (
              <span>
                This form has no saved theme, so public pages render in the standard
                styling. Use the Themes tab to change it.
              </span>
            )}
            {hasTheme && (
              <Button small text="Reset theme" action={resetTheme} />
            )}
            {themeStatus === "saving" && (
              <span className="text-xs text-slate-500">Resetting…</span>
            )}
            {themeStatus === "error" && (
              <span className="text-xs text-sg-red">Reset failed</span>
            )}
            {themeError && (
              <span className="text-xs text-sg-red">{themeError}</span>
            )}
          </div>

          {/* The Creator's own toast lasts two seconds and the property grid's
              error strip renders blank for reasons we cannot fix from here, so
              this is where an upload or save failure actually stays readable. */}
          {uploadError && (
            <p className="mt-3 text-xs text-sg-red">{uploadError}</p>
          )}
          {saveError && (
            <p className="mt-3 text-xs text-sg-red">{saveError}</p>
          )}

          <p className="mt-3 text-xs text-slate-500">
            <strong>Images:</strong> you can upload the survey logo and the page
            background directly — {IMAGE_UPLOAD_HINT} Pasting a public{" "}
            <code>https://</code> image URL into the same field still works.
            Cover/advanced theme headers aren't supported yet.
          </p>
        </Card>
      )}

      <div style={{ height: "100%", width: "92%", position: "absolute" }}>
        {
          <SurveyCreatorComponent
            creator={creator}
            style={{ height: "100%" }}
          />
        }
      </div>
    </>
  );
}
